/**
 * Das Sendungssuchfeld - ARIA-Combobox-Muster wie `NewOrder/JtlSuchfeld.tsx` (Schwesterbaum, Task
 * 23), hier fuer Sendungen statt JTL-Stammdaten. Nutzt `jtlSuche` (`src/api/jtlSuche.ts`, lokale
 * Kopie - siehe deren Kopfkommentar) fuer Debounce/Mindestlaenge/Cache/Single-Flight-Guard (Global
 * Constraint 16, Brief: "sucht Sendungen mit denselben Vorgaben wie jedes andere Suchfeld").
 *
 * Rendert sein EIGENES `<label>` (anders als `JtlSuchfeld`, das dem Aufrufer die Beschriftung
 * ueberlaesst) - `SendungsZuordnung` braucht keine zusaetzliche Huelle, der Komponententest greift
 * direkt ueber `getByLabelText("Sendung suchen")`.
 *
 * ================= GENERISCH statt fest auf `SendungsTreffer` (Auftrag "Reiter Spedition") =====
 *
 * Der Auftrag fuer den neuen Drawer-Reiter "Spedition" verlangt woertlich, diese Komponente "auf
 * `SendungsTreffer`" umzuziehen (`../../lib/types.ts` - die Serverform der neuen Sendungssuche,
 * `dsvShipmentNo`/`containerNo`/`consignee`/...). Ein einfaches Umbenennen des Feldtyps geht nicht
 * ohne Nebenwirkung: `SendungsZuordnung.tsx` (derselbe Baum, AUSDRUECKLICH nicht anzufassen - siehe
 * dessen Kopfkommentar und den Task-Auftrag) reicht dieser Komponente weiterhin ihren EIGENEN,
 * lokalen `Sendung`-Typ herein (`trackingNo`/`dischargePort`, kein `dsvShipmentNo`). Ein `suche`/
 * `onAuswahl` fest auf `SendungsTreffer` typisiert waere dort ein Typfehler (nachgemessen: `tsc`
 * meldet "Type 'Sendung' is missing ... dsvShipmentNo, masterBlAwb, ...") - UND liefe zur Laufzeit
 * gegen `test/komponenten/Shipments/zuordnung.test.tsx` (Fall "sucht Sendungen..."), das die
 * Trefferliste heute ueber `trackingNo` erwartet.
 *
 * Geloest ueber ein generisches `T`, das NUR die Felder verlangt, die diese Komponente selbst
 * anfasst - alle bewusst OPTIONAL, damit sowohl der lokale `Sendung`-Typ (mit Catch-All-
 * Indexsignatur) als auch `SendungsTreffer` (alle Felder PFLICHT) ihn erfuellen. Der neue
 * Konsument (`Drawer/ShipTab/DsvSendung.tsx`) instanziiert `T = SendungsTreffer` - genau das ist
 * die geforderte Umstellung. Fuer den TYPCHECK bricht das den bestehenden Aufrufer nicht - fuer die
 * DARSTELLUNG tat es das zunaechst doch (Befund einer Pruefung, 2026-08-21): die Trefferzeile
 * fuehrte vorher `trackingNo — dischargePort`, `bezeichnung()` unten kennt `dischargePort` nicht
 * (nur `containerNo`/`consignee`, Felder von `SendungsTreffer`) und liess die Zeile in
 * `SendungsZuordnung.tsx` seither auf den blossen `trackingNo` schrumpfen.
 *
 * Behoben ueber die optionale Prop `beschriftung` (Vorgabe: `bezeichnung()` unten, also die neue
 * Form) - `SendungsZuordnung.tsx` reicht ihre EIGENE Beschriftungsfunktion herein und bekommt damit
 * `trackingNo — dischargePort` zurueck, ohne dass dieses Modul `dischargePort` kennen muesste.
 *
 * ================= Trefferzeile mit Substanz (Auftrag „Sendungs-Dropdown, Portal-Link,
 * Konfliktdialog", 2026-08-21, Ziel 2) =================
 *
 * `bezeichnung()` lieferte bis hierher EINE Textzeile (`dsvShipmentNo — containerNo · consignee`).
 * Kuenftig zweizeilig plus einer Marke je verknuepfter Bestellung:
 *
 *   SRAF0238041 · MSKU1234567 · 20GP                    [ Bestellung 10397 ]
 *   ETA 12.09.2026 · Zhejiang Foo Co. → ABSINA GmbH · 611 Colli · 28,1 m³
 *
 * Deshalb weitet sich der Rueckgabetyp der Prop `beschriftung` von `string` auf `ReactNode`
 * (rueckwaertskompatibel: eine Zeichenkette IST ein `ReactNode` - `SendungsZuordnung.tsx` bleibt
 * unveraendert und reicht weiterhin eine string-liefernde Funktion herein, siehe deren
 * Kopfkommentar). Jedes Feld einer `SendungsTreffer`-Zeile kann `null` sein (laut Messung vom
 * 21.08.2026 vermutlich DAUERHAFT bei `masterBlAwb`/`bookingRef`/den Haefen) - `text()` unten ist
 * der gemeinsame Nullfilter, der weder "null"/"undefined" noch einen einzeln stehenden Trenner
 * sichtbar werden laesst.
 *
 * Die Hervorhebung des Suchtreffers (`hervorgehoben()`) ist aus `NewOrder/JtlSuchfeld.tsx`
 * UEBERNOMMEN (dort nicht exportiert, deshalb hier eine eigene Kopie) - der Mitarbeiter soll sehen,
 * WARUM eine Zeile getroffen wurde, nicht nur DASS sie es wurde.
 *
 * ================= Lieferant, Warenbeschreibung, mehrere Container, DSV-Bestellnummern (Auftrag
 * „Sendungsfelder", 2026-08-22) =================
 *
 * `shipper`/`consignee`/`packages` blieben in Produktion leer, weil das BACKEND sie falsch zuordnete
 * (paralleler Auftrag, gleicher Tag - repariert dort). Dieser Auftrag liefert dazu vier weitere
 * Felder (`SendungsTreffer` in `lib/types.ts`) und baut die Zeile neu:
 *
 *   SSZX71146160 · HLBU2158664 +1 · 20            [ DSV: 10397 +3 ]  [ Bestellung 10316 ]
 *   NINGBO XUANSHI ELECTRONICS · POWER CORD
 *   ETA 11.08.2026 · 611 Colli · 15,65 m³
 *
 * Drei Aenderungen gegenueber Ziel 2 oben:
 * - Zeile 1 zeigt bei MEHREREN Containern (`containerNos`, Payload-Reihenfolge) nur den ERSTEN plus
 *   `+n` (`containerAnzeige()`) - alle auszuschreiben wuerde die Zeile in der schmalen Drawer-Liste
 *   sprengen. `containerNo` (Einzahl) bleibt der Fallback fuer Aufrufer ohne `containerNos`
 *   (`SendungsZuordnung.tsx`s lokaler `Sendung`-Typ).
 * - NEUE Zeile 2 - `shipper` (Lieferant) und `goodsDescription` (Warenbeschreibung): das ist die
 *   Zeile, an der ein Mensch die Sendung wiedererkennt, nicht die (durchgaengig „ABSINA GmbH" und
 *   damit nichtssagende) `consignee`. `consignee` wird deshalb NIRGENDS mehr angezeigt.
 *   `goodsDescription` kann laut Messung mehrzeilig sein - `warenbeschreibung()` ersetzt
 *   Zeilenumbrueche durch Leerzeichen (EINE Zeile im Markup), die Kuerzung selbst passiert per CSS
 *   (`text-overflow: ellipsis` auf `.sz-suche-zeile2`, `neubau/stationen.css`), NICHT hier - ein
 *   hartes JS-`slice()` wuerde bei jeder Schriftgroesse/Fensterbreite falsch abschneiden.
 * - Die bisherige Zeile 2 (Zeit/Colli/Volumen) rueckt zu Zeile 3, ohne die entfernte `consignee`.
 * - NEUE Marke `dsvBestellnummern` (`dsvMarke()`) - die von DSV mitgelieferten Bestellnummern, ERSTE
 *   plus `+n`, vollstaendige Liste im `title`. Eigene Klasse `sz-suche-marke-dsv` (zusaetzlich zu
 *   `sz-suche-marke`): „laut DSV" ist eine Behauptung der Spedition, eine `verknuepfteBestellungen`-
 *   Marke dagegen eine Tatsache im Hub - beide muessen optisch unterscheidbar bleiben, deshalb keine
 *   gemeinsame Klasse.
 * - `shippingMarks` wird von dieser Komponente bewusst NICHT gelesen/angezeigt (reiner, langer/
 *   kryptischer Suchtext) - deshalb fehlt es auch in `SendungsTrefferAehnlich` unten, das nur Felder
 *   fuehrt, die diese Komponente selbst anfasst.
 *
 * ================= Tastatursteuerung (Ziel 2) =================
 *
 * Bis hierher liess sich die Trefferliste nur mit der Maus bedienen - ergaenzt nach dem Vorbild
 * `JtlSuchfeld.tsx#beiTaste`: Pfeiltasten (mit Umlauf), Home/End springen an den Rand, Enter waehlt
 * NUR bei vorhandener Tastatur-Hervorhebung (sonst schluckt das Feld ein umschliessendes Formular-
 * Absenden), Escape schliesst. `aria-activedescendant` zeigt den hervorgehobenen Eintrag an, wie im
 * Vorbild.
 *
 * ================= Sofortiges Laden beim Fokussieren, keine Mindestlaenge mehr (Nachbesserung D2,
 * 2026-08-22) =================
 *
 * Bislang musste erst getippt werden (und das auch erst ab zwei Zeichen, `JTL_SUCHE_MINDESTLAENGE`
 * aus `api/jtlSuche.ts`), bevor ueberhaupt eine Anfrage lief. Der Nutzer will beim blossen
 * Hineinklicken sofort etwas sehen ("damit man schneller eine Idee bekommt, was man sucht"). Zwei
 * Aenderungen, beide NUR fuer diese Komponente (nicht `JtlSuchfeld.tsx` - dort bleibt die
 * Mindestlaenge, siehe Auftrag):
 *
 * - `gedrosselteSuche` wird mit `mindestlaenge: 0` gebaut - `""` (leeres Feld) UND ein einzelnes
 *   Zeichen loesen jetzt eine Anfrage aus, statt `Promise.resolve([])` ohne jeden Serverkontakt.
 *   Das Backend liefert bei leerem/einzeichigem `q` kuenftig die zuletzt bewegten Sendungen statt
 *   einer leeren Liste (Backend-Zusage, paralleler Auftrag) - GENAU das zeigt der neue `onFocus`.
 * - `beiFokus()` ist eine schlanke Kopie von `beiEingabe()`, OHNE `setWert`/`setAktiv`-Reset (der
 *   Feldinhalt aendert sich beim Fokussieren nicht) und ueber DIESELBE `gedrosselteSuche`-Instanz -
 *   ein zweiter Fokus auf denselben Begriff (typischerweise `""`) trifft also denselben Cache-
 *   Eintrag wie ein zweiter Tastendruck und loest KEINE zweite Anfrage an `suche` aus (kein
 *   Ladegewitter beim Durchtabben). Derselbe `angefordert.current`-Stale-Guard wie beim Tippen
 *   verhindert, dass eine ueberholte Fokus-Antwort eine zwischenzeitliche Eingabe ueberschreibt.
 * - `zeigeListe` (`!disabled && offen && optionen.length > 0`) blieb UNVERAENDERT - eine leere
 *   Antwort setzt zwar `offen=true`, das `<ul>` rendert aber ohnehin nur bei mindestens einem
 *   Treffer. Kein leeres Kaestchen noetig, kein Sonderfall.
 * - `disabled` sperrt `beiFokus()` genauso wie `beiEingabe()` - dieselbe Gegenprobe-Begruendung
 *   (ein `disabled`-Feld kann per `fireEvent`/programmatisch trotzdem ein `focus`-Event bekommen).
 * - Kein Scrollen: `beiFokus()` ruehrt weder `aktiv` noch sonst einen Scroll-Trigger an - die Liste
 *   kann also nicht ueber Inhalte springen, die der Nutzer gerade liest.
 */
import {
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { jtlSuche, normalisiereSuchbegriff } from "../../api/jtlSuche.ts";
import { fmtDateNum } from "../../lib/datumsAnzeige.ts";
import { meldeFehler } from "../../state/fehlerStapel.ts";

/** Die Felder, auf die diese Komponente selbst zugreift - siehe Kopfkommentar. */
export interface SendungsTrefferAehnlich {
  id?: unknown;
  /** `SendungsTreffer.dsvShipmentNo` - primaere Beschriftung eines Treffers. */
  dsvShipmentNo?: unknown;
  /** Fallback fuer den bestehenden Aufrufer `SendungsZuordnung.tsx` (lokaler `Sendung`-Typ). */
  trackingNo?: unknown;
  containerNo?: unknown;
  /** NEU (Ziel 2) - Zeile 1. */
  containerType?: unknown;
  /** NEU (Sendungsfelder, 2026-08-22) - Zeile 1, ALLE Container statt nur dem ersten (siehe
   *  `containerAnzeige()`). Fallback bei Fehlen: `containerNo` oben. */
  containerNos?: unknown;
  consignee?: unknown;
  /** NEU - Zeile 2. */
  shipper?: unknown;
  /** NEU (Sendungsfelder, 2026-08-22) - Zeile 2, Warenbeschreibung (siehe `warenbeschreibung()`). */
  goodsDescription?: unknown;
  /** NEU - Zeile 2, Rueckfall wenn `eta` leer ist. */
  etd?: unknown;
  /** NEU - Zeile 2. */
  eta?: unknown;
  /** NEU - Zeile 2, als "… Colli". */
  packages?: unknown;
  /** NEU - Zeile 2, als "… m³". */
  volumeM3?: unknown;
  /**
   * NEU - die rechte Marke je Eintrag ("Bestellung {jtlOrderId}"/"Entwurf"). Bewusst `unknown`
   * (nicht `VerknuepfteBestellung[]`): dieselbe Begruendung wie bei den uebrigen Feldern - der
   * lokale `Sendung`-Typ in `SendungsZuordnung.tsx` fuehrt das Feld gar nicht, `marken()` unten
   * prueft deshalb selbst mit `Array.isArray`.
   */
  verknuepfteBestellungen?: unknown;
  /**
   * NEU (Sendungsfelder, 2026-08-22) - die LINKE Marke, die von DSV mitgelieferten Bestellnummern
   * (siehe `dsvMarke()`). Bewusst `unknown`, dieselbe Begruendung wie bei `verknuepfteBestellungen`.
   */
  dsvBestellnummern?: unknown;
}

export interface SendungsSucheProps<T extends SendungsTrefferAehnlich> {
  /** Roher Serverabruf fuer EINEN Suchbegriff. Wird hier per `jtlSuche` gedrosselt/gecacht. */
  suche: (begriff: string) => Promise<T[]>;
  onAuswahl: (sendung: T) => void;
  /**
   * Die Trefferzeile - optional, Vorgabe `bezeichnung()` unten. `SendungsZuordnung.tsx` reicht
   * hier ihre eigene Form (`trackingNo — dischargePort`) herein, siehe Kopfkommentar. Rueckgabetyp
   * `ReactNode` statt `string` seit Ziel 2 (2026-08-21) - rueckwaertskompatibel, siehe Kopfkommentar.
   */
  beschriftung?: (treffer: T) => ReactNode;
  /**
   * Sperrt das Feld: kein Tippen, keine Suche, keine offene Liste (C2, `Drawer/ShipTab/
   * DsvSendung.tsx` setzt sie waehrend `laeuft`, siehe deren Kopfkommentar).
   */
  disabled?: boolean;
}

/** `unknown` -> getrimmter Text oder `null`, wenn leer/nicht vorhanden - der gemeinsame Nullfilter
 *  jedes Feldes der Trefferzeile (Ziel 2: kein "null"/"undefined", kein einzeln stehender Trenner). */
function text(wert: unknown): string | null {
  if (wert === null || wert === undefined) return null;
  const s = String(wert).trim();
  return s === "" ? null : s;
}

const ZAHL_DE = new Intl.NumberFormat("de-DE");

/**
 * Hervorhebung des Suchtreffers - UEBERNOMMEN aus `NewOrder/JtlSuchfeld.tsx#hervorgehoben` (dort
 * modulintern, nicht exportiert; deshalb hier eine eigene Kopie, siehe Kopfkommentar). Zerlegt
 * `wert` am ERSTEN Vorkommen des normalisierten `begriff`, der Treffer geht in `<b>`.
 */
function hervorgehoben(wert: string, begriff: string): ReactNode {
  const suchbegriff = normalisiereSuchbegriff(begriff);
  if (suchbegriff === "") return wert;
  const stelle = wert.toLowerCase().indexOf(suchbegriff);
  if (stelle === -1) return wert;
  const vorher = wert.slice(0, stelle);
  const treffer = wert.slice(stelle, stelle + suchbegriff.length);
  const nachher = wert.slice(stelle + suchbegriff.length);
  return (
    <>
      {vorher}
      <b>{treffer}</b>
      {nachher}
    </>
  );
}

/** Reiht die nicht-leeren `teile` mit " · " aneinander - `null` faellt samt seinem Trenner weg. */
function zeileAusTeilen(teile: readonly (string | null)[], begriff: string): ReactNode {
  const sichtbar = teile.filter((t): t is string => t !== null);
  return sichtbar.map((t, index) => (
    <span key={index}>
      {index > 0 ? " · " : ""}
      {hervorgehoben(t, begriff)}
    </span>
  ));
}

/** Zeile 2, Zeitangabe: `eta`, sonst `etd` als "ETD …"; fehlen beide, entfaellt der Abschnitt. */
function zeitangabe(treffer: SendungsTrefferAehnlich): string | null {
  const eta = text(treffer.eta);
  if (eta) return `ETA ${fmtDateNum(eta)}`;
  const etd = text(treffer.etd);
  return etd ? `ETD ${fmtDateNum(etd)}` : null;
}

/** Zeile 3, Colli-Anzahl - `null` bei fehlendem oder nicht-endlichem Wert. */
function colli(treffer: SendungsTrefferAehnlich): string | null {
  if (treffer.packages === null || treffer.packages === undefined) return null;
  const zahl = Number(treffer.packages);
  return Number.isFinite(zahl) ? `${ZAHL_DE.format(zahl)} Colli` : null;
}

/** Zeile 3, Volumen in m³ - dieselbe Vorsicht wie bei `colli()`. */
function volumen(treffer: SendungsTrefferAehnlich): string | null {
  if (treffer.volumeM3 === null || treffer.volumeM3 === undefined) return null;
  const zahl = Number(treffer.volumeM3);
  return Number.isFinite(zahl) ? `${ZAHL_DE.format(zahl)} m³` : null;
}

/** `unknown` -> Liste getrimmter, nicht-leerer Zeichenketten - der gemeinsame Listenfilter fuer
 *  `containerNos`/`dsvBestellnummern` (beide laut `lib/types.ts` immer Arrays, leer statt `null`;
 *  hier trotzdem mit `Array.isArray` abgesichert, siehe `SendungsTrefferAehnlich`s Kopfkommentare). */
function textListe(wert: unknown): string[] {
  if (!Array.isArray(wert)) return [];
  return wert
    .map((eintrag) => text(eintrag))
    .filter((eintrag): eintrag is string => eintrag !== null);
}

/** Zeile 1, Container: der ERSTE aus `containerNos`, weitere als `+n` angedeutet (nicht ausschreiben
 *  - die Zeile muss in der schmalen Drawer-Liste lesbar bleiben). Fallback auf die Einzahl
 *  `containerNo`, wenn `containerNos` fehlt (`SendungsZuordnung.tsx`s lokaler `Sendung`-Typ). */
function containerAnzeige(treffer: SendungsTrefferAehnlich): string | null {
  const liste = textListe(treffer.containerNos);
  if (liste.length === 0) return text(treffer.containerNo);
  const rest = liste.length - 1;
  return rest > 0 ? `${liste[0]} +${rest}` : liste[0]!;
}

/** Zeile 2, Warenbeschreibung: laut Messung KANN mehrzeilig sein - auf EINE Zeile gezwungen
 *  (Zeilenumbrueche zu Leerzeichen). Die Kuerzung selbst passiert per CSS (`text-overflow: ellipsis`
 *  auf `.sz-suche-zeile2`), NICHT hier - siehe Kopfkommentar. */
function warenbeschreibung(treffer: SendungsTrefferAehnlich): string | null {
  const roh = text(treffer.goodsDescription);
  return roh === null ? null : roh.replace(/[\r\n]+/g, " ");
}

/** Die rechte Marke je verknuepfter Bestellung ("Bestellung {jtlOrderId}"/"Entwurf") - eine
 *  TATSACHE im Hub, deshalb die schlichte `sz-suche-marke`-Klasse ohne Zusatz. */
function bestellungsMarken(treffer: SendungsTrefferAehnlich): ReactNode[] {
  const liste = Array.isArray(treffer.verknuepfteBestellungen) ? treffer.verknuepfteBestellungen : [];
  return liste.map((eintrag, index) => {
    const jtlOrderId = (eintrag as { jtlOrderId?: unknown } | null | undefined)?.jtlOrderId;
    const inhalt =
      jtlOrderId === null || jtlOrderId === undefined ? "Entwurf" : `Bestellung ${String(jtlOrderId)}`;
    return (
      <span key={`b-${index}`} className="sz-suche-marke">
        {inhalt}
      </span>
    );
  });
}

/** Die linke Marke fuer die von DSV mitgelieferten Bestellnummern - ERSTE plus `+n`, vollstaendige
 *  Liste im `title` (die verdeckten Werte bleiben so ueber den Tooltip erreichbar). Eigene Klasse
 *  `sz-suche-marke-dsv`: „laut DSV" ist eine BEHAUPTUNG der Spedition, keine Tatsache im Hub -
 *  optisch unterscheidbar von `bestellungsMarken()` oben. */
function dsvMarke(treffer: SendungsTrefferAehnlich): ReactNode {
  const liste = textListe(treffer.dsvBestellnummern);
  if (liste.length === 0) return null;
  const rest = liste.length - 1;
  const inhalt = rest > 0 ? `DSV: ${liste[0]} +${rest}` : `DSV: ${liste[0]}`;
  return (
    <span className="sz-suche-marke sz-suche-marke-dsv" title={liste.join(", ")}>
      {inhalt}
    </span>
  );
}

/** Beide Markenarten nebeneinander - DSV zuerst (Zielbild), dann je verknuepfter Bestellung. */
function marken(treffer: SendungsTrefferAehnlich): ReactNode {
  const dsv = dsvMarke(treffer);
  const bestellungen = bestellungsMarken(treffer);
  if (!dsv && bestellungen.length === 0) return null;
  return (
    <span className="sz-suche-marken">
      {dsv}
      {bestellungen}
    </span>
  );
}

/**
 * Vorgabe fuer `beschriftung` - drei Zeilen plus Marken, siehe Kopfkommentar. `begriff` ist der
 * AKTUELLE Feldinhalt (fuer die Hervorhebung); anders als bei `JtlSuchfeld.tsx#beschriftung` (Pflicht,
 * ein Argument) bleibt die OEFFENTLICHE Prop-Signatur bei einem Argument - `begriff` wird beim Aufruf
 * unten aus dem Komponentenzustand herein-geschlossen (siehe `zeigeTreffer`).
 */
function bezeichnung(treffer: SendungsTrefferAehnlich, begriff: string): ReactNode {
  const haupt = text(treffer.dsvShipmentNo ?? treffer.trackingNo);
  const zeile1 = zeileAusTeilen(
    [haupt, containerAnzeige(treffer), text(treffer.containerType)],
    begriff,
  );

  // Zeile 2 (NEU): Lieferant und Warenbeschreibung - die Zeile, an der ein Mensch die Sendung
  // wiedererkennt. `consignee` traegt bewusst NICHTS bei (durchgaengig „ABSINA GmbH").
  const zeile2Teile = [text(treffer.shipper), warenbeschreibung(treffer)];
  const zeile2 = zeileAusTeilen(zeile2Teile, begriff);
  const hatZeile2 = zeile2Teile.some((t) => t !== null);

  // Zeile 3 (vormals Zeile 2 aus Ziel 2): Zeit/Colli/Volumen.
  const zeile3Teile = [zeitangabe(treffer), colli(treffer), volumen(treffer)];
  const zeile3 = zeileAusTeilen(zeile3Teile, begriff);
  const hatZeile3 = zeile3Teile.some((t) => t !== null);

  return (
    <span className="sz-suche-treffer">
      <span className="sz-suche-kopf">
        <span className="sz-suche-zeile1">{zeile1}</span>
        {marken(treffer)}
      </span>
      {hatZeile2 && <span className="sz-suche-zeile2">{zeile2}</span>}
      {hatZeile3 && <span className="sz-suche-zeile3">{zeile3}</span>}
    </span>
  );
}

export function SendungsSuche<T extends SendungsTrefferAehnlich>({
  suche,
  onAuswahl,
  beschriftung,
  disabled,
}: SendungsSucheProps<T>) {
  const [wert, setWert] = useState("");
  const [optionen, setOptionen] = useState<T[]>([]);
  const [offen, setOffen] = useState(false);
  /** Index der per Tastatur hervorgehobenen Option; -1 = keine - siehe Kopfkommentar. */
  const [aktiv, setAktiv] = useState(-1);
  const angefordert = useRef("");
  const reactId = useId();
  const listboxId = `${reactId}-optionen`;
  const optionId = (index: number): string => `${listboxId}-${index}`;

  // Vorgabe faellt auf `bezeichnung(treffer, wert)` zurueck - der aktuelle Feldinhalt fliesst als
  // Suchbegriff fuer die Hervorhebung ein. Wird `beschriftung` gereicht (SendungsZuordnung.tsx),
  // bleibt es bei genau EINEM Argument - siehe Kopfkommentar zur Rueckwaertskompatibilitaet.
  const zeigeTreffer = beschriftung ?? ((treffer: T) => bezeichnung(treffer, wert));

  // Eine `jtlSuche`-Instanz fuer die Lebensdauer dieser Suchfeld-Instanz - Cache/Single-Flight bleibt
  // ueber Tastenanschlaege hinweg erhalten, solange `suche` dieselbe Funktionsreferenz bleibt.
  // `mindestlaenge: 0` (Nachbesserung D2) - siehe Kopfkommentar: weder ein leeres Feld (Fokus) noch
  // ein einzelnes Zeichen (Tippen) werden mehr client-seitig abgewiesen.
  const gedrosselteSuche = useMemo(() => jtlSuche<T>({ abruf: suche, mindestlaenge: 0 }), [suche]);

  /**
   * Ablehnungszweig beider Suchwege (Fehleranzeige Paket 1, Spec R6): vorher blieb ein gescheiterter
   * Abruf eine unbehandelte Ablehnung, und die Liste erschien einfach nicht. Der Fehlerstapel fasst
   * dieselbe Meldung zu EINEM Eintrag mit Zaehler zusammen (Review Focus 1). Gemeldet wird nur die
   * Ablehnung zur ZULETZT gestellten Eingabe - derselbe Stale-Guard wie in den Erfolgszweigen (Review
   * Task 5): sonst bliebe eine Meldung zu einer ueberholten Eingabe im Stapel stehen, waehrend die
   * aktuelle Suche laengst Treffer zeigt. `jtlSuche` weist einer Entprellung samt aller ueberholten
   * Aufrufe dieselbe Ablehnung zu; nur der aktuelle Aufruf meldet sie.
   */
  function meldeSucheFehlgeschlagen(fehler: unknown): void {
    meldeFehler(fehler, "Die Sendungssuche konnte nicht ausgeführt werden.");
  }

  function beiEingabe(text: string): void {
    // `disabled` sperrt das native Feld bereits gegen echte Tastatureingabe - dieser Riegel ist die
    // Gegenprobe fuer einen programmatischen `onChange` (C2).
    if (disabled) return;
    setWert(text);
    setOffen(true);
    setAktiv(-1);
    angefordert.current = text;
    gedrosselteSuche(text).then(
      (treffer) => {
        // Stale-Guard: nur die Antwort auf die ZULETZT gestellte Eingabe darf die Liste fuellen.
        if (angefordert.current !== text) return;
        setOptionen(treffer);
      },
      (fehler) => {
        if (angefordert.current !== text) return;
        meldeSucheFehlgeschlagen(fehler);
      },
    );
  }

  /**
   * Laedt beim blossen Fokussieren, ohne Tastendruck (Nachbesserung D2) - siehe Kopfkommentar.
   * Bewusst OHNE `setWert`/`setAktiv`: der Feldinhalt bleibt, wie er ist. `zeigeListe` unten
   * rendert die Liste ohnehin erst bei mindestens einem Treffer - eine leere Antwort oeffnet
   * deshalb kein leeres Kaestchen, ohne dass es hier eines Sonderfalls beduerfte.
   */
  function beiFokus(): void {
    // `disabled` sperrt auch den Fokus-Ladeweg - dieselbe Gegenprobe-Begruendung wie in
    // `beiEingabe()`: ein programmatisches `focus`-Event erreicht ein `disabled`-Feld trotzdem.
    if (disabled) return;
    setOffen(true);
    angefordert.current = wert;
    gedrosselteSuche(wert).then(
      (treffer) => {
        // Stale-Guard wie in `beiEingabe()`: eine ueberholte Fokus-Antwort darf eine zwischenzeitliche
        // Eingabe nicht mehr ueberschreiben.
        if (angefordert.current !== wert) return;
        setOptionen(treffer);
      },
      (fehler) => {
        if (angefordert.current !== wert) return;
        meldeSucheFehlgeschlagen(fehler);
      },
    );
  }

  function waehle(treffer: T): void {
    onAuswahl(treffer);
    setWert(String(treffer.dsvShipmentNo ?? treffer.trackingNo ?? ""));
    setOptionen([]);
    setOffen(false);
    setAktiv(-1);
  }

  const zeigeListe = !disabled && offen && optionen.length > 0;

  /** Pfeiltasten/Home/End/Enter/Escape - Vorbild `JtlSuchfeld.tsx#beiTaste` (Ziel 2). */
  function beiTaste(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Escape") {
      setOffen(false);
      setAktiv(-1);
      return;
    }
    if (!zeigeListe) {
      // Pfeiltaste bei geschlossener, aber gefuellter Liste oeffnet sie wieder.
      if ((event.key === "ArrowDown" || event.key === "ArrowUp") && optionen.length > 0) {
        event.preventDefault();
        setOffen(true);
        setAktiv(event.key === "ArrowDown" ? 0 : optionen.length - 1);
      }
      return;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setAktiv((alt) => (alt + 1) % optionen.length);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setAktiv((alt) => (alt <= 0 ? optionen.length - 1 : alt - 1));
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      setAktiv(0);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      setAktiv(optionen.length - 1);
      return;
    }
    // ENTER NUR MIT HERVORHEBUNG - sonst schluckt das Feld das Absenden eines umschliessenden
    // Formulars, wie im Vorbild.
    if (event.key === "Enter" && aktiv >= 0 && aktiv < optionen.length) {
      event.preventDefault();
      waehle(optionen[aktiv]!);
    }
  }

  return (
    <div className="sz-suche">
      <label>
        Sendung suchen
        <input
          role="combobox"
          type="text"
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={zeigeListe}
          aria-controls={listboxId}
          aria-activedescendant={zeigeListe && aktiv >= 0 ? optionId(aktiv) : undefined}
          value={wert}
          disabled={disabled}
          onChange={(event) => beiEingabe(event.target.value)}
          onFocus={beiFokus}
          onBlur={() => setOffen(false)}
          onKeyDown={beiTaste}
        />
      </label>
      {zeigeListe && (
        <ul className="sz-suche-optionen" role="listbox" id={listboxId}>
          {optionen.map((treffer, index) => (
            <li
              key={(treffer.id as string) ?? `${reactId}-${index}`}
              id={optionId(index)}
              role="option"
              aria-selected={index === aktiv}
              // onMouseDown statt onClick: verhindert, dass ein vorheriges onBlur (Fokuswechsel) die
              // Liste schliesst, bevor der Klick ausgewertet wird - wie in `JtlSuchfeld.tsx`.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => waehle(treffer)}
            >
              {zeigeTreffer(treffer)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
